import { Popover as AntPopover, PopoverProps as AntPopoverProps } from 'antd';
import FocusTrap from 'focus-trap-react';
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

export interface IPopoverProps extends AntPopoverProps {
  children: React.ReactElement<HTMLButtonElement>;
  content: React.ReactNode;
}

export interface IPopoverHandle {
  close: () => void;
}

/**
 * Popover personalizzato che gestisce l'accessibilità, se è necessario
 * quindi il focus e la gestione del click esterno.
 */
export const Popover = forwardRef(
  (props: IPopoverProps, ref: React.Ref<IPopoverHandle>) => {
    const { children, content, ...otherProps } = props;
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(ref, () => ({
      close: () => {
        setIsOpen(false);
      },
    }));

    return (
      <AntPopover
        {...otherProps}
        trigger={['click']}
        open={isOpen}
        destroyTooltipOnHide={{
          keepParent: true,
        }}
        onOpenChange={nextIsOpen => {
          setIsOpen(nextIsOpen);
        }}
        content={
          <FocusTrap
            active={isOpen}
            focusTrapOptions={{
              escapeDeactivates: () => {
                setIsOpen(false);
                return true;
              },
              fallbackFocus: () => containerRef.current!,
              setReturnFocus: previous => {
                return previous ?? buttonRef.current!;
              },
            }}
          >
            <div tabIndex={-1} ref={containerRef}>
              {content}
            </div>
          </FocusTrap>
        }
      >
        {React.cloneElement(children as React.ReactElement, {
          ref: buttonRef,
        })}
      </AntPopover>
    );
  },
);

Popover.displayName = 'Popover';
